import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormatting } from '../hooks/useFormatting';
import { PoliticianData, BenchmarkData } from '../types';
import ShareCardModal from './ShareCardModal';
import './PoliticianCard.css';

interface PoliticianCardProps {
  politician: PoliticianData;
  benchmarks: Record<string, BenchmarkData>;
  timeframe?: string;
  onClose: () => void;
}

const PoliticianCard: React.FC<PoliticianCardProps> = ({
  politician,
  benchmarks,
  timeframe = '1Y',
  onClose,
}) => {
  const { t, i18n } = useTranslation();
  const { formatCurrency, formatPercentage, formatDate } = useFormatting();
  const [activeTab, setActiveTab] = useState<'holdings' | 'simulatedTrades' | 'disclosure'>('holdings');
  const [showShareModal, setShowShareModal] = useState(false);
  const returnPct = ((politician.currentValue - 10000) / 10000) * 100;
  const isPositive = returnPct >= 0;

  // Get localized politician name
  const getLocalizedName = () => {
    if (i18n.language.startsWith('zh')) {
      const chineseName = t(`politician:names.${politician.id}`, { defaultValue: '' });
      return chineseName ? `${chineseName} (${politician.name})` : politician.name;
    }
    return politician.name;
  };

  const spyFinal = benchmarks.SPY.data[benchmarks.SPY.data.length - 1]?.value || 10000;
  const qqqFinal = benchmarks.QQQ.data[benchmarks.QQQ.data.length - 1]?.value || 10000;

  const spyReturn = ((spyFinal - 10000) / 10000) * 100;
  const qqqReturn = ((qqqFinal - 10000) / 10000) * 100;

  const vsSpy = returnPct - spyReturn;
  const vsQqq = returnPct - qqqReturn;

  const partyColor = politician.party === 'Democrat' ? '#3b82f6' : '#ef4444';
  const partyKey = politician.party.toLowerCase() as 'democrat' | 'republican';
  const chamberKey = politician.chamber.toLowerCase() as 'house' | 'senate';

  return (
    <div className="politician-card" style={{ '--accent': politician.color } as React.CSSProperties}>
      <div className="card-actions">
        <button
          className="share-card-button"
          onClick={() => setShowShareModal(true)}
        >
          {t('shareCard:share')}
        </button>
        <button className="close-button" onClick={onClose}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M12 4L4 12M4 4L12 12"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      <div className="card-header">
        <div className="avatar-container">
          <img
            src={`/images/${politician.id}.jpg`}
            alt={politician.name}
            className="avatar-image"
            onError={(e) => {
              (e.target as HTMLImageElement).src = '/images/placeholder.png';
            }}
          />
          <div className="avatar-glow" style={{ backgroundColor: politician.color }} />
        </div>

        <div className="header-info">
          <h3 className="politician-name">{getLocalizedName()}</h3>
          <div className="politician-meta">
            <span className="party-badge" style={{ color: partyColor }}>
              {t(`politician:${partyKey}`)}
            </span>
            <span className="separator">|</span>
            <span>{politician.state}</span>
            <span className="separator">|</span>
            <span>{t(`politician:${chamberKey}`)}</span>
          </div>
        </div>
      </div>

      <div className="card-body">
        <div className="main-stat">
          <div className="stat-label">{t('politician:simulatedPortfolioValue')}</div>
          <div className="stat-main-value">
            {formatCurrency(politician.currentValue)}
          </div>
          <div className={`stat-return ${isPositive ? 'positive' : 'negative'}`}>
            {formatPercentage(returnPct)}
            <span className="return-label">{t('politician:from')} $10,000</span>
          </div>
        </div>

        <div className="comparison-section">
          <div className="section-title">{t('politician:vsBenchmarks')}</div>
          <div className="comparison-grid">
            <div className="comparison-item">
              <span className="comparison-label">{t('politician:vsSPY')}</span>
              <span className={`comparison-value ${vsSpy >= 0 ? 'positive' : 'negative'}`}>
                {formatPercentage(vsSpy)}
              </span>
            </div>
            <div className="comparison-item">
              <span className="comparison-label">{t('politician:vsQQQ')}</span>
              <span className={`comparison-value ${vsQqq >= 0 ? 'positive' : 'negative'}`}>
                {formatPercentage(vsQqq)}
              </span>
            </div>
          </div>
        </div>

        <div className="tabs-section">
          <div className="tabs-header">
            <button
              className={`tab-button ${activeTab === 'holdings' ? 'active' : ''}`}
              onClick={() => setActiveTab('holdings')}
            >
              {t('politician:holdings')}
            </button>
            <button
              className={`tab-button ${activeTab === 'simulatedTrades' ? 'active' : ''}`}
              onClick={() => setActiveTab('simulatedTrades')}
            >
              {t('politician:trades')}
            </button>
            <button
              className={`tab-button ${activeTab === 'disclosure' ? 'active' : ''}`}
              onClick={() => setActiveTab('disclosure')}
            >
              {t('politician:disclosure')}
            </button>
          </div>

          {activeTab === 'holdings' && politician.topHoldings && politician.topHoldings.length > 0 && (
            <div className="holdings-list">
              {politician.topHoldings.slice(0, 3).map((holding, idx) => (
                <div key={holding.ticker} className="holding-row">
                  <span className="holding-rank">{idx + 1}</span>
                  <span className="holding-ticker">{holding.ticker}</span>
                  <span className="holding-pct">{holding.percentage.toFixed(1)}%</span>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'simulatedTrades' && (
            <div className="trades-list">
              {politician.trades && politician.trades.length > 0 ? (
                politician.trades
                  .slice()
                  .sort((a, b) => new Date(b.pub_date).getTime() - new Date(a.pub_date).getTime())
                  .slice(0, 100)
                  .map((trade, idx) => (
                    <div key={`${trade.pub_date}-${trade.ticker}-${idx}`} className="trade-row">
                      <span className="trade-date">{formatDate(trade.pub_date, 'MMM d, yyyy')}</span>
                      <span className="trade-ticker">{trade.ticker}</span>
                      <span className={`trade-type ${trade.trade_type.toLowerCase()} ${trade.trade_count === 0 ? 'rebalance' : ''}`}>
                        {trade.trade_type === 'Buy' ? t('politician:buy') : t('politician:sell')}
                        {trade.trade_count > 1 && (
                          <span className="trade-count-badge">{trade.trade_count}</span>
                        )}
                        {trade.trade_count === 0 && (
                          <span className="rebalance-badge">⚖</span>
                        )}
                      </span>
                      <span className="trade-amount">
                        <span className="trade-percentage">{trade.portfolio_percentage.toFixed(1)}%</span>
                        <span className="trade-separator">|</span>
                        <span className="trade-dollars">{formatCurrency(trade.simulated_amount, true)}</span>
                      </span>
                    </div>
                  ))
              ) : (
                <div className="empty-state">{t('politician:noTrades')}</div>
              )}
            </div>
          )}

          {activeTab === 'disclosure' && (
            <div className="disclosures-list">
              {politician.rawDisclosures && politician.rawDisclosures.length > 0 ? (
                politician.rawDisclosures.map((disclosure, index) => (
                  <div key={index} className={`disclosure-row ${disclosure.ticker_source === 'unmappable' ? 'unmappable' : ''}`}>
                    <div className="disclosure-header">
                      <span className="disclosure-date">
                        {formatDate(disclosure.pub_date, 'MMM d, yyyy')}
                      </span>
                      <span className={`disclosure-type ${disclosure.trade_type.toLowerCase()}`}>
                        {disclosure.trade_type.toUpperCase()}
                      </span>
                    </div>
                    <div className="disclosure-company">
                      {disclosure.issuer}
                      {disclosure.mapped_ticker ? (
                        <span className="disclosure-ticker">
                          {' '}({disclosure.mapped_ticker})
                          {disclosure.ticker_source === 'etf_proxy' && (
                            <span className="etf-proxy-badge" title="Simulated using ETF proxy"> ETF</span>
                          )}
                        </span>
                      ) : disclosure.ticker && disclosure.ticker !== 'N/A' ? (
                        <span className="disclosure-ticker"> ({disclosure.ticker})</span>
                      ) : disclosure.ticker_source === 'unmappable' ? (
                        <span className="disclosure-ticker unmappable-ticker"> (Private)</span>
                      ) : null}
                    </div>
                    <div className="disclosure-details">
                      <span className="disclosure-owner">
                        {t('politician:owner')}: {disclosure.owner}
                      </span>
                      <span className="disclosure-value-range">
                        {t('politician:valueRange')}: {disclosure.value_range}
                      </span>
                    </div>
                    {disclosure.trade_date !== disclosure.pub_date && (
                      <div className="disclosure-trade-date">
                        {t('politician:tradeDate')}: {formatDate(disclosure.trade_date, 'MMM d, yyyy')}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="empty-state">{t('politician:noDisclosures')}</div>
              )}
            </div>
          )}
        </div>

      </div>

      {showShareModal && (
        <ShareCardModal
          politician={politician}
          benchmarks={benchmarks}
          timeframe={timeframe}
          onClose={() => setShowShareModal(false)}
        />
      )}
    </div>
  );
};

export default PoliticianCard;
